/**
 * DayDetailView
 *
 * Shows all meals for a given day with backward-scheduled prep timelines.
 * Users can see exactly when to start prepping/cooking each meal.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  Pressable,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useSelector, useDispatch } from 'react-redux';
import Feather from '@expo/vector-icons/Feather';
import { useTranslation } from 'react-i18next';
import { RootState, AppDispatch } from '../../redux/store';
import { selectMealsByDate, PlannedMeal, MealType, markMealAsCooked, unmarkMealAsCooked } from '../../redux/mealPlanSlice';
import { isEntryMeal } from '../../redux/mealEntry';
import { selectDayPreference, DayPreference, DEFAULT_DAY_PREFERENCE, selectDismissedFallbackMealIds } from '../../redux/userPreferencesSlice';
import { selectCookingSession } from '../../redux/cookingSessionSlice';
import { selectRecipeVerdicts } from '../../redux/recipeMetadataSlice';
import CookedWithoutModeSheet from '../cookingPlan/CookedWithoutModeSheet';
import { addFeedbackEvent } from '../../redux/feedbackSlice';
import { createMealCompletedEvent } from '../../services/feedbackEventHelpers';
import { calculatePrepTimeline, formatTimeDisplay, PrepTimeline } from '../../utils/prepTimeline';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
  PaprikaShadows,
} from '../../styles/theme';
import PantryMatchStrip from './PantryMatchStrip';
import { RemoteImage } from '../shared/RemoteImage';
import { todayIso } from '../../utils/localDate';

// Mealtime chip options (30-min increments from 5:00 PM to 8:00 PM)
const MEALTIME_OPTIONS = [
  '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00',
];

interface DayDetailViewProps {
  date: string; // YYYY-MM-DD
}

const DayDetailView: React.FC<DayDetailViewProps> = ({ date }) => {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const userId = user?.uid ?? undefined;

  // Parse date info
  const dateObj = useMemo(() => new Date(date + 'T12:00:00'), [date]);
  const isTodayOrPast = date <= todayIso();
  const dayOfWeek = dateObj.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

  // Get meals for this day from Redux
  const meals = useSelector((state: RootState) => selectMealsByDate(state, date));
  const currentWeekStart = useSelector((state: RootState) => state.mealPlan?.currentWeekStart);
  const session = useSelector(selectCookingSession);
  const dismissedFallbackMealIds = useSelector(selectDismissedFallbackMealIds);

  // Get day preference for default mealtime
  const dayPref: DayPreference = useSelector(selectDayPreference(dayOfWeek));
  const defaultMealTime = dayPref?.defaultMealTime || DEFAULT_DAY_PREFERENCE.defaultMealTime;

  // Local override for target time (session only, not persisted)
  const [targetTimeOverride, setTargetTimeOverride] = useState<string | null>(null);
  const targetTime = targetTimeOverride || defaultMealTime;

  // Format the date for display
  const dateDisplay = useMemo(() => {
    const locale = i18n.language === 'fr' ? 'fr-CA' : 'en-US';
    return dateObj.toLocaleDateString(locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  }, [dateObj, i18n.language]);

  // Dinner meals with recipes (for cooking plan CTA)
  const dinnerMealsWithRecipe = useMemo(
    () => meals.filter(m => m.mealType === 'dinner' && m.recipe),
    [meals],
  );

  // Check if there's an active session for a dinner meal on this day
  const hasActiveSessionForDay = dinnerMealsWithRecipe.some(m => m.id === session.mealId) && session.screen !== 'done';

  // Compute fallback meal for past uncooked meals
  const today = todayIso();
  const isPastDay = date < today;
  const fallbackMeal = isPastDay
    ? dinnerMealsWithRecipe.find(
        m =>
          m.status !== 'cooked' &&
          session.mealId !== m.id &&
          !dismissedFallbackMealIds.includes(m.id),
      ) ?? null
    : null;
  const [sheetDismissed, setSheetDismissed] = useState(false);
  const showFallbackSheet = fallbackMeal !== null && !sheetDismissed;

  // Group meals by type
  const mealGroups = useMemo(() => {
    const groups: { type: MealType; label: string; meals: PlannedMeal[] }[] = [];

    if (isWeekend) {
      const breakfastMeals = meals.filter(m => m.mealType === 'breakfast');
      if (breakfastMeals.length > 0) {
        groups.push({ type: 'breakfast', label: t('thisWeek.mealTypes.breakfast'), meals: breakfastMeals });
      }
    }

    const lunchMeals = meals.filter(m => m.mealType === 'lunch');
    if (lunchMeals.length > 0) {
      groups.push({ type: 'lunch', label: t('thisWeek.mealTypes.lunch'), meals: lunchMeals });
    }

    const dinnerMeals = meals.filter(m => m.mealType === 'dinner');
    if (dinnerMeals.length > 0) {
      groups.push({ type: 'dinner', label: t('thisWeek.mealTypes.dinner'), meals: dinnerMeals });
    }

    return groups;
  }, [meals, isWeekend]);

  // Handle meal press — navigate to recipe detail
  const handleMealPress = (meal: PlannedMeal) => {
    if (meal.recipeId === 'free-text' || isEntryMeal(meal)) return;
    router.push({
      pathname: `/(app)/recipe-detail/${meal.recipeId}`,
      params: {
        plannedServings: String(meal.servings),
        slotDate: date,
        slotMealType: meal.mealType,
        ...(meal.recipe ? { recipeData: JSON.stringify(meal.recipe) } : {}),
      },
    });
  };

  // Handle mark done / undo
  const handleMarkDone = useCallback((meal: PlannedMeal) => {
    if (meal.status === 'cooked') {
      dispatch(unmarkMealAsCooked({ mealId: meal.id }));
    } else {
      dispatch(markMealAsCooked({ mealId: meal.id }));
      if (!isEntryMeal(meal) && meal.recipeId !== 'free-text' && meal.recipe && currentWeekStart) {
        dispatch(addFeedbackEvent(
          createMealCompletedEvent(currentWeekStart, meal.recipeId, meal.recipe.title, meal.servings)
        ));
      }
    }
  }, [dispatch, currentWeekStart]);

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
          <Feather name="arrow-left" size={24} color={Paprika.textPrimary} />
        </TouchableOpacity>
        <View style={styles.headerTextContainer}>
          <Text style={styles.headerTitle}>{dateDisplay}</Text>
        </View>
        <View style={styles.backButton} />
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Target Time Picker */}
        <View style={styles.timePickerSection}>
          <Text style={styles.timePickerLabel}>{t('thisWeek.dinnerAt')}</Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.timeChipsContainer}
          >
            {MEALTIME_OPTIONS.map(time => {
              const isActive = time === targetTime;
              return (
                <TouchableOpacity
                  key={time}
                  style={[styles.timeChip, isActive && styles.timeChipActive]}
                  onPress={() => setTargetTimeOverride(time)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.timeChipText, isActive && styles.timeChipTextActive]}>
                    {formatTimeDisplay(time, i18n.language)}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>

        {/* Meal Groups */}
        {mealGroups.length > 0 ? (
          mealGroups.map(group => (
            <View key={group.type} style={styles.mealGroup}>
              <Text style={styles.mealGroupTitle}>{group.label}</Text>
              {group.meals.map(meal => (
                <MealTimelineCard
                  key={meal.id}
                  meal={meal}
                  targetTime={targetTime}
                  onPress={() => handleMealPress(meal)}
                  onMarkDone={isTodayOrPast ? () => handleMarkDone(meal) : undefined}
                />
              ))}
            </View>
          ))
        ) : (
          <View style={styles.emptyState}>
            <Feather name="calendar" size={48} color={Paprika.textMuted} />
            <Text style={styles.emptyStateText}>{t('thisWeek.noMealsForDay')}</Text>
          </View>
        )}

        {/* Cooking plan CTA */}
        {dinnerMealsWithRecipe.length >= 1 && (
          <TouchableOpacity
            style={styles.cookingPlanCTA}
            onPress={() => router.push(`/(app)/cooking-plan/${date}`)}
            activeOpacity={0.7}
          >
            <Feather
              name={hasActiveSessionForDay ? "play" : "clock"}
              size={16}
              color={Paprika.accent}
            />
            <Text style={styles.cookingPlanCTAText}>
              {hasActiveSessionForDay ? t('cookingPlan.resume') : t('cookingPlan.planThisMeal')}
            </Text>
            <Feather name="arrow-right" size={16} color={Paprika.accent} />
          </TouchableOpacity>
        )}
      </ScrollView>

      {showFallbackSheet && fallbackMeal && (
        <CookedWithoutModeSheet
          meal={fallbackMeal}
          userId={userId}
          onDismiss={() => setSheetDismissed(true)}
        />
      )}
    </View>
  );
};

/**
 * MealTimelineCard — Shows a meal with its prep timeline
 */
interface MealTimelineCardProps {
  meal: PlannedMeal;
  targetTime: string;
  onPress: () => void;
  onMarkDone?: () => void;
}

const MealTimelineCard: React.FC<MealTimelineCardProps> = ({ meal, targetTime, onPress, onMarkDone }) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const recipe = meal.recipe;

  // Calculate timeline
  const timeline: PrepTimeline = useMemo(() => {
    if (!recipe) return { type: 'unavailable' };
    return calculatePrepTimeline(
      {
        preparationMinutes: recipe.preparationMinutes,
        cookingMinutes: recipe.cookingMinutes,
        readyInMinutes: recipe.readyInMinutes,
      },
      targetTime,
    );
  }, [recipe, targetTime]);

  const verdicts = useSelector((state: RootState) =>
    selectRecipeVerdicts(state, String(meal.recipeId))
  );
  const hasVerdict = Object.keys(verdicts).length > 0;

  const title = recipe?.title || meal.freeText || t('thisWeek.untitledMeal');
  const imageUrl = recipe?.image;
  const isCooked = meal.status === 'cooked';
  const isFreeText = meal.recipeId === 'free-text';
  // Gated on the household's verdicts, not the legacy per-meal number: that
  // number is no longer written or displayed anywhere, so a cooked meal would
  // have kept inviting a rating forever.
  const showRateLink = isCooked && !isFreeText && !hasVerdict;

  return (
    <TouchableOpacity
      style={[styles.mealCard, isCooked && styles.mealCardCooked]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      {/* Recipe Info Row */}
      <View style={styles.mealCardHeader}>
        <RemoteImage
          uri={imageUrl}
          variant="thumb"
          style={styles.mealCardImage}
          fallback={<View style={[styles.mealCardImage, styles.mealCardImagePlaceholder]}>
              <Feather name="book-open" size={20} color={Paprika.textMuted} />
            </View>}
        />
        <View style={styles.mealCardInfo}>
          <Text style={styles.mealCardTitle} numberOfLines={2}>{title}</Text>
          {recipe?.readyInMinutes ? (
            <Text style={styles.mealCardMeta}>
              {t('thisWeek.minTotal', { count: recipe.readyInMinutes })}
              {recipe.preparationMinutes && recipe.cookingMinutes
                ? ` ${t('thisWeek.prepAndCook', { prep: recipe.preparationMinutes, cook: recipe.cookingMinutes })}`
                : ''}
            </Text>
          ) : null}
        </View>
        <Feather name="chevron-right" size={20} color={Paprika.textMuted} />
      </View>

      {/* Pantry Match Strip */}
      <PantryMatchStrip meal={meal} />

      {/* Timeline */}
      <View style={styles.timelineContainer}>
        {timeline.type === 'full' && (
          <>
            <TimelineStep
              label={t(timeline.blocks[0].labelKey)}
              time={formatTimeDisplay(timeline.blocks[0].time, language)}
              isFirst
              dotColor={isCooked ? Paprika.success : Paprika.accent}
              showCheck={isCooked}
            />
            <TimelineStep
              label={t(timeline.blocks[1].labelKey)}
              time={formatTimeDisplay(timeline.blocks[1].time, language)}
              dotColor={isCooked ? Paprika.success : Paprika.accentOrange}
              showCheck={isCooked}
            />
            <TimelineStep
              label={isCooked ? t('thisWeek.enjoyed') : t(timeline.blocks[2].labelKey)}
              time={formatTimeDisplay(timeline.blocks[2].time, language)}
              isLast
              dotColor={Paprika.success}
              showCheck={isCooked}
            />
          </>
        )}
        {timeline.type === 'partial' && (
          <>
            <TimelineStep
              label={t(timeline.blocks[0].labelKey)}
              time={formatTimeDisplay(timeline.blocks[0].time, language)}
              isFirst
              dotColor={isCooked ? Paprika.success : Paprika.accent}
              showCheck={isCooked}
            />
            <TimelineStep
              label={isCooked ? t('thisWeek.enjoyed') : t(timeline.blocks[1].labelKey)}
              time={formatTimeDisplay(timeline.blocks[1].time, language)}
              isLast
              dotColor={Paprika.success}
              showCheck={isCooked}
            />
          </>
        )}
        {timeline.type === 'unavailable' && (
          <Text style={styles.timelineUnavailable}>{t('thisWeek.timingNotAvailable')}</Text>
        )}
      </View>

      {/* Mark as done / Rate this */}
      <View style={styles.completionRow}>
        {!isCooked && onMarkDone && (
          <Pressable onPress={(e) => { e.stopPropagation?.(); onMarkDone(); }} style={styles.markDoneButton}>
            <Feather name="check-circle" size={16} color={Paprika.success} />
            <Text style={styles.markDoneText}>{t('thisWeek.markAsDone')}</Text>
          </Pressable>
        )}
        {isCooked && onMarkDone && (
          <Pressable onPress={(e) => { e.stopPropagation?.(); onMarkDone(); }} style={styles.undoDoneButton}>
            <Feather name="rotate-ccw" size={14} color={Paprika.textMuted} />
            <Text style={styles.undoDoneText}>{t('thisWeek.undo')}</Text>
          </Pressable>
        )}
        {showRateLink && (
          <Text style={styles.rateLinkText}>{t('thisWeek.rateThis')} &rarr;</Text>
        )}
      </View>
    </TouchableOpacity>
  );
};

/**
 * TimelineStep — A single dot + line step in the vertical timeline
 */
interface TimelineStepProps {
  label: string;
  time: string;
  isFirst?: boolean;
  isLast?: boolean;
  dotColor: string;
  showCheck?: boolean;
}

const TimelineStep: React.FC<TimelineStepProps> = ({ label, time, isFirst, isLast, dotColor, showCheck }) => {
  return (
    <View style={styles.timelineStep}>
      {/* Timeline rail (dot + line) */}
      <View style={styles.timelineRail}>
        {!isFirst && <View style={[styles.timelineLineAbove, showCheck && styles.timelineLineCooked]} />}
        {showCheck ? (
          <View style={[styles.timelineDot, styles.timelineDotCheck, { backgroundColor: dotColor }]}>
            <Feather name="check" size={8} color="#fff" />
          </View>
        ) : (
          <View style={[styles.timelineDot, { backgroundColor: dotColor }]} />
        )}
        {!isLast && <View style={[styles.timelineLineBelow, showCheck && styles.timelineLineCooked]} />}
      </View>

      {/* Content */}
      <View style={styles.timelineContent}>
        <Text style={styles.timelineTime}>{time}</Text>
        <Text style={styles.timelineLabel}>{label}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: PaprikaSpacing.md,
    paddingTop: Platform.OS === 'web' ? PaprikaSpacing.md : PaprikaSpacing.xxl,
    paddingBottom: PaprikaSpacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.borderLight,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTextContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    ...PaprikaTypography.titleLarge,
    color: Paprika.textPrimary,
  },

  // Scroll
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: PaprikaSpacing.lg,
    paddingBottom: PaprikaSpacing.xxl,
  },

  // Time Picker
  timePickerSection: {
    marginBottom: PaprikaSpacing.lg,
  },
  timePickerLabel: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.sm,
  },
  timeChipsContainer: {
    gap: PaprikaSpacing.xs,
  },
  timeChip: {
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.xs,
    borderRadius: PaprikaRadius.xl,
    backgroundColor: Paprika.surfaceLight,
    borderWidth: 1,
    borderColor: Paprika.borderLight,
  },
  timeChipActive: {
    backgroundColor: Paprika.accentLight,
    borderColor: Paprika.accent,
  },
  timeChipText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
  },
  timeChipTextActive: {
    color: Paprika.accent,
    fontWeight: '600',
  },

  // Meal Group
  mealGroup: {
    marginBottom: PaprikaSpacing.lg,
  },
  mealGroupTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.sm,
  },

  // Meal Card
  mealCardCooked: {
    opacity: 0.7,
  },
  mealCard: {
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.md,
    borderWidth: 1,
    borderColor: Paprika.borderLight,
    padding: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.sm,
    ...PaprikaShadows.card,
  },
  mealCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
  },
  mealCardImage: {
    width: 56,
    height: 56,
    borderRadius: PaprikaRadius.sm,
  },
  mealCardImagePlaceholder: {
    backgroundColor: Paprika.surfaceLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mealCardInfo: {
    flex: 1,
  },
  mealCardTitle: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
  },
  mealCardMeta: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
    marginTop: 2,
  },

  // Timeline
  timelineContainer: {
    marginTop: PaprikaSpacing.md,
    paddingTop: PaprikaSpacing.sm,
    borderTopWidth: 1,
    borderTopColor: Paprika.borderLight,
  },
  timelineStep: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 40,
  },
  timelineRail: {
    width: 24,
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  timelineLineAbove: {
    width: 2,
    flex: 1,
    backgroundColor: Paprika.border,
  },
  timelineDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  timelineDotCheck: {
    width: 14,
    height: 14,
    borderRadius: 7,
    justifyContent: 'center',
    alignItems: 'center',
  },
  timelineLineCooked: {
    backgroundColor: Paprika.success,
  },
  timelineLineBelow: {
    width: 2,
    flex: 1,
    backgroundColor: Paprika.border,
  },
  timelineContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: PaprikaSpacing.sm,
    gap: PaprikaSpacing.sm,
  },
  timelineTime: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
    fontWeight: '600',
    minWidth: 80,
  },
  timelineLabel: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
  },
  timelineUnavailable: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textMuted,
    fontStyle: 'italic',
    paddingVertical: PaprikaSpacing.xs,
  },

  // Completion actions
  completionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: PaprikaSpacing.sm,
    paddingTop: PaprikaSpacing.xs,
  },
  markDoneButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.xs,
    paddingVertical: PaprikaSpacing.xs,
  },
  markDoneText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.success,
    fontWeight: '600',
  },
  undoDoneButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.xs,
    paddingVertical: PaprikaSpacing.xs,
  },
  undoDoneText: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
  },
  rateLinkText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.accent,
    fontWeight: '600',
    marginLeft: 'auto',
  },

  // Cooking Plan CTA
  cookingPlanCTA: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    paddingVertical: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.md,
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.md,
    marginTop: PaprikaSpacing.xs,
  },
  cookingPlanCTAText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.accent,
    flex: 1,
  },

  // Empty State
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: PaprikaSpacing.xxl * 2,
    gap: PaprikaSpacing.md,
  },
  emptyStateText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textMuted,
  },
});

export default DayDetailView;
